import React, { useState, useEffect, useCallback } from 'react';
import { get } from 'aws-amplify/api';
import LoadingSpinner from './LoadingSpinner';
import InlineSpinner from './InlineSpinner';
import { format } from 'date-fns';

// Constants for error handling
const ERROR_TYPES = {
  NETWORK: 'network',
  API: 'api',
  DATA: 'data',
  UNKNOWN: 'unknown'
};

// Maximum number of retry attempts
const MAX_RETRIES = 3;

// Check if we're in a test environment
const isTestEnv = process.env.NODE_ENV === 'test';

function PaginatedAuditLogList({ 
  resourceId, 
  resourceType,
  pageSize = 10,
  filters = {},
  logsDisabled = false,
  viewType = null // New parameter to specify the view type: 'invitations', 'guests' intended when no other filter is in place
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Helper function to build the path for the new format
  const buildLogsPath = useCallback((token = null) => {
    // Start building the path components
    const pathComponents = [];
    
    // Handle special view types for event logs
    if (resourceType === 'event' && viewType) {
      pathComponents.push('event');
      pathComponents.push(resourceId);
      pathComponents.push(viewType);
      /**      
      if (viewType === 'guest' && filters.guest_id) {
        // not used
        // Path for specific guest responses to an event: /logs/event/$event_id/guest/$guest_id
        pathComponents.push('guest');
        pathComponents.push(filters.guest_id);
      } else if (viewType === 'invitations') {
        // Path for all invitation activity on an event: /logs/event/$event_id/invitations
        pathComponents.push('invitations');
      } else if (viewType === 'guests') {
        // Path for all guest activity on an event: /logs/event/$event_id/guests
        pathComponents.push('guests');
      }
      **/
    } else {
      // Standard path format for other resource types
      if (resourceType && resourceId) {
        pathComponents.push(resourceType.toLowerCase());
        pathComponents.push(resourceId);
      }
      
      // Add additional filters if provided
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'limit' && !(viewType === 'guest' && key === 'guest_id')) {
          pathComponents.push(key.toLowerCase());
          pathComponents.push(value);
        }
      });
    }
    
    // Add page size
    pathComponents.push(pageSize.toString());
    
    // Add pagination token if available - properly encode it to handle special characters
    if (token) {
      // Ensure the token is properly URL encoded to handle JSON special characters
      const encodedToken = encodeURIComponent(token);
      pathComponents.push(encodedToken);
    }
    
    // Construct the final path
    return `/logs/${pathComponents.join('/')}`;
  }, [resourceType, resourceId, filters, pageSize, viewType]);

  const fetchAuditLogs = useCallback(async (token = null, retryAttempt = 0) => {
    // Check if logs are disabled
    if (logsDisabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorType(null);
      
      // Validate required parameters
      if (!resourceId || !resourceType) {
        throw new Error('Missing required parameters: resourceId or resourceType');
      }
      
      // Build the path using the new format
      const path = buildLogsPath(token);
      
      // Make API request
      let restOperation;
      try {
        restOperation = get({
          apiName: 'events',
          path: path,
          withCredentials: true
        });
      } catch (apiError) {
        // Handle network errors
        console.error('Network error fetching audit logs:', apiError);
        throw { 
          type: ERROR_TYPES.NETWORK, 
          message: 'Network error while fetching logs. Please check your connection.',
          originalError: apiError
        };
      }

      // Check for API errors
      if (!restOperation || restOperation.statusCode >= 400) {
        const statusCode = restOperation?.statusCode || 'unknown';
        throw { 
          type: ERROR_TYPES.API, 
          message: `API error (${statusCode}) while fetching logs.`,
          statusCode
        };
      }
      
      // Parse response data
      let data;
      try {
        const { body } = await restOperation.response;
        data = await body.json();
      } catch (parseError) {
        throw { 
          type: ERROR_TYPES.DATA, 
          message: 'Failed to parse log data.',
          originalError: parseError
        };
      }
      
      // Validate response data
      if (!data || !Array.isArray(data.items)) {
        throw { 
          type: ERROR_TYPES.DATA,
          message: 'Invalid log data format received.'
        };
      }
      
      // Check if count property exists to validate the expected format
      if (typeof data.count !== 'number') {
        console.warn('API response missing count property or count is not a number');
      }
      
      // Update state with new data
      setLogs(prevLogs => token ? [...prevLogs, ...data.items] : data.items);
      setNextToken(data.next_token || null);
      setRetryCount(0); // Reset retry count on success
      
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      
      // Determine error type and message
      const errorType = err.type || ERROR_TYPES.UNKNOWN;
      let errorMessage = err.message || 'Failed to load audit logs';
      
      // Set error state
      setErrorType(errorType);
      
      // Retry for network errors if under max retries and not in test environment
      if (errorType === ERROR_TYPES.NETWORK && retryAttempt < MAX_RETRIES && !isTestEnv) {
        console.log(`Retrying fetch (attempt ${retryAttempt + 1} of ${MAX_RETRIES})...`);
        setRetryCount(retryAttempt + 1);
        
        // Exponential backoff for retries
        const backoffTime = Math.min(1000 * Math.pow(2, retryAttempt), 8000);
        
        setTimeout(() => {
          fetchAuditLogs(token, retryAttempt + 1);
        }, backoffTime);
        
        return;
      }
      
      // If we've exhausted retries or it's not a network error
      setError(errorMessage);
      
    } finally {
      if (retryCount === 0) {
        setLoading(false);
      }
    }
  }, [resourceId, resourceType, pageSize, filters, logsDisabled, buildLogsPath]);

  useEffect(() => {
    // Reset state when resource, filters, or viewType change 
    setLogs([]);
    setNextToken(null);
    setError(null);
    setErrorType(null);
    setRetryCount(0);
    fetchAuditLogs(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, resourceType, pageSize, ...Object.values(filters), logsDisabled, viewType]);

  // Helper function to generate the appropriate title based on viewType
  const getLogTitle = useCallback(() => {
    if (viewType === 'guest' && filters.guest_id) {
      return "Guest Responses";
    } else if (viewType === 'invitations') {
      return "Invitation Activity";
    } else if (viewType === 'guests') {
      return "Guest Activity";
    }
    return "Activity Log";
  }, [viewType, filters.guest_id]);

  const loadMore = () => {
    if (nextToken) {
      fetchAuditLogs(nextToken);
    }
  };

  const retry = () => {
    setError(null);
    setErrorType(null);
    fetchAuditLogs(nextToken);
  };

  // Render based on component state
  if (logsDisabled) {
    return (
      <div className="content--spaced">
        <h3 className="heading heading--md">{getLogTitle()}</h3>
        <div className="text text--muted">Audit logs are currently disabled.</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content--spaced">
        <h3 className="heading heading--md">{getLogTitle()}</h3>
        <div className="text-red-600">
          <p>{error}</p>
          <button 
            onClick={retry}
            className="button button--secondary mt-2"
            data-testid="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading && !logs.length) {
    return <LoadingSpinner message="Loading audit logs..." />;
  }

  if (!logs.length) {
    return (
      <div className="content--spaced">
        <h3 className="heading heading--md">{getLogTitle()}</h3>
        <div className="text text--muted">No audit logs available</div>
      </div>
    );
  }

  return (
    <div className="content--spaced">
      <h3 className="heading heading--md">{getLogTitle()}</h3>
      <div className="content__list">
        {logs.map((log) => (
          <div key={log.id || `log-${Math.random()}`} className="content__item">
            <div className="content__row">
              <div>
                <p className="text text--sm">{log.details || 'No details available'}</p>
                <p className="text text--xs text--muted">
                  {log.action || 'Unknown action'} by {log.user_id || 'Unknown user'} from {log.ip_address || 'Unknown location'}
                </p>
              </div>
              <span className="text text--xs text--muted">
                {log.timestamp ? format(new Date(log.timestamp), 'PPpp') : 'Unknown time'}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {loading && <div style={{textAlign: 'center', padding: '20px'}}><InlineSpinner message="Loading more logs..." /></div>}
      
      {nextToken && !loading && (
        <button
          onClick={loadMore}
          className="button button--secondary button--full"
        >
          Load More
        </button>
      )}
    </div>
  );
}

export default PaginatedAuditLogList;









